import { t } from "i18next";

import type {
  ChildClassification,
  DurationType,
  MediaChildren,
  MediaTranslation,
} from "../api/MediaAPI.types";
import type { TranslationsIndexes } from "../types";
import type { NewMediaTranslation } from "../types/hostedMedia";

class HostedMedia {
  type: string;

  uuid: string;

  hasTemporaryId: boolean;

  child_classification: ChildClassification;

  childKey?: string;

  label: string;

  duration: number;

  duration_type?: DurationType;

  translations: MediaTranslation[] | [NewMediaTranslation];

  translations_indexes: TranslationsIndexes;

  constructor({
    item,
    languageId,
    classification,
    label,
  }: {
    item?: MediaChildren;
    languageId: string;
    classification: ChildClassification;
    label?: string;
  }) {
    this.type = "hosted";
    this.uuid = item?.uuid ?? "temp_id";
    this.hasTemporaryId = this.uuid === "temp_id";
    this.child_classification = item?.child_classification ?? classification;
    this.childKey = item?.child_classification?.key;
    this.label = item?.label ?? label ?? this.generateLabel();
    this.duration = item?.duration ?? 0;
    this.duration_type = item?.duration_type;
    this.translations =
      item?.translations ?? HostedMedia.newTranslations(languageId);
    this.translations_indexes =
      item?.translations_indexes ??
      HostedMedia.newTranslationsIndexes(languageId);
  }

  setId(id: string): HostedMedia {
    this.uuid = id;

    return this;
  }

  setTranslationId(id: string): HostedMedia {
    this.translations[0].uuid = id;

    return this;
  }

  setDuration(duration: number): HostedMedia {
    this.duration = duration;

    return this;
  }

  setDurationType(durationType: DurationType): HostedMedia {
    this.duration_type = durationType;

    return this;
  }

  getTranslation(
    property: keyof MediaTranslation,
  ): MediaTranslation[keyof MediaTranslation] {
    const defaultLanguageIndex = this.translations_indexes?.default?.true[0];

    if (
      defaultLanguageIndex === undefined ||
      !this.translations[defaultLanguageIndex]
    )
      return null;

    return this.translations[defaultLanguageIndex][property];
  }

  generateLabel(): string {
    return (this.getTranslation("name") ??
      t(this.childKey as string, { ns: "editor-content" })) as string;
  }

  static newTranslations(languageId: string): [NewMediaTranslation] {
    return [
      {
        default: true,
        language: "",
        language_uuid: languageId,
        description: "",
        short_description: "",
        name: "",
        slug: "",
      },
    ];
  }

  static newTranslationsIndexes(languageId: string): TranslationsIndexes {
    return {
      language_uuid: {
        [languageId]: [0],
      },
      default: {
        true: [0],
      },
    };
  }

  static TYPES = {
    UNDEFINED: "",
    LIVE: "live",
    HOSTED: "hosted",
    EXTERNAL: "external",
    MASTER: "master",
  };
}

export default HostedMedia;
